import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DescontoGrupo } from "@atlas/types";
import { Button, fmtBRL } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import "./desconto-grupos.css";

const valorPago = (grupo: DescontoGrupo): number => grupo.origens[0]?.pagoCentavos ?? 0;
const valorDistribuido = (grupo: DescontoGrupo): number =>
  grupo.alocacoes.reduce((total, alocacao) => total + alocacao.valorCentavos, 0);

function competenciaLegivel(competencia: string): string {
  return /^\d{6}$/.test(competencia)
    ? `${competencia.slice(4, 6)}/${competencia.slice(0, 4)}`
    : competencia;
}

function Estado({ grupo }: { grupo: DescontoGrupo }) {
  const distribuido = grupo.estado === "distribuido";
  return (
    <span className={`dg-status ${distribuido ? "dg-status--ok" : "dg-status--pending"}`}>
      <span aria-hidden="true">{distribuido ? "✓" : "◷"}</span>
      {distribuido ? "Distribuído" : "Pendente"}
    </span>
  );
}

export function DescontoGrupos({ folhaId, onClose }: { folhaId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["grupos-desconto", folhaId],
    queryFn: () => atlas.gruposDesconto.listar(folhaId),
  });
  const [selecionado, selecionar] = useState<string | null>(null);
  const historicoCompleto = query.data?.grupos ?? [];
  const grupos = historicoCompleto.filter((grupo) => grupo.estado !== "substituido");
  const grupoSelecionado = grupos.find((grupo) => grupo.id === selecionado)
    ?? grupos.find((grupo) => grupo.estado === "pendente")
    ?? grupos[0];
  const competencia = grupoSelecionado?.competencia ?? grupos[0]?.competencia ?? "";
  const pendentes = grupos.filter((grupo) => grupo.estado === "pendente").length;
  const descontado = grupos.reduce((total, grupo) => total + valorPago(grupo), 0);
  const distribuido = grupos.reduce((total, grupo) => total + valorDistribuido(grupo), 0);

  const atualizar = () => {
    void qc.invalidateQueries({ queryKey: ["grupos-desconto", folhaId] });
    void qc.invalidateQueries({ queryKey: ["admin-folhas"] });
    void qc.invalidateQueries({ queryKey: ["admin", "folhas"] });
  };

  return (
    <section aria-label="Distribuição dos descontos" className="desconto-grupos">
      <button type="button" className="dg-breadcrumb" onClick={onClose}>
        Averbadora <span>/</span> Folhas <span>/</span> Distribuição
      </button>

      <header className="dg-header">
        <div>
          <div className="dg-title-row">
            <h1>Distribuição dos descontos</h1>
            {competencia && <span className="dg-competencia">Competência {competenciaLegivel(competencia)}</span>}
          </div>
          <p>Confira os valores da folha e distribua os descontos entre os contratos.</p>
        </div>
        <Button type="button" variant="ghost" onClick={onClose}>Voltar às folhas</Button>
      </header>

      {pendentes > 0 && (
        <div className="dg-page-alert" role="status">
          <span aria-hidden="true">⚠</span>
          Existem {pendentes} {pendentes === 1 ? "pendência que impede" : "pendências que impedem"} o fechamento.
        </div>
      )}

      {query.isLoading && <div className="dg-card dg-loading">Carregando lançamentos…</div>}
      {query.error && <div className="dg-card dg-error" role="alert">{query.error.message}</div>}

      {!query.isLoading && !query.error && grupos.length === 0 && (
        <div className="dg-card dg-empty-page">
          <span aria-hidden="true">✓</span>
          <div><strong>Nenhum desconto para distribuir</strong><p>Esta folha não possui grupos de desconto.</p></div>
        </div>
      )}

      {grupos.length > 0 && (
        <>
          <div className="dg-totals" aria-label="Resumo dos valores">
            <ResumoValor rotulo="Descontado pela ALECE" centavos={descontado} />
            <ResumoValor rotulo="Distribuído pela ATLAS" centavos={distribuido} />
            <ResumoValor rotulo="Saldo a distribuir" centavos={descontado - distribuido} destaque={descontado !== distribuido} />
          </div>

          <div className="dg-card dg-list-card">
            <div className="dg-card-heading">
              <h2>Lançamentos da folha</h2>
              <span className={pendentes ? "dg-count dg-count--pending" : "dg-count"}>
                {pendentes ? `${pendentes} pendente${pendentes === 1 ? "" : "s"}` : "Tudo distribuído"}
              </span>
            </div>
            <div className="dg-table-scroll">
              <table className="dg-table">
                <thead><tr>
                  <th>Matrícula</th><th>Rubrica</th><th className="dg-money">Descontado pela ALECE</th>
                  <th className="dg-money">Distribuído pela ATLAS</th><th>Status</th><th>Ação</th>
                </tr></thead>
                <tbody>{grupos.map((grupo) => {
                  const origem = grupo.origens[0];
                  const ativo = grupo.id === grupoSelecionado?.id;
                  return (
                    <tr key={grupo.id} className={ativo ? "dg-row--active" : undefined}>
                      <td className="dg-mono">{origem?.matricula || "—"}</td>
                      <td><strong>{origem?.rubrica || "—"}</strong><span className="dg-rubrica-desc"> — {origem?.descricao || "Sem descrição"}</span></td>
                      <td className="dg-money">{fmtBRL(valorPago(grupo) / 100)}</td>
                      <td className="dg-money">{fmtBRL(valorDistribuido(grupo) / 100)}</td>
                      <td><Estado grupo={grupo} /></td>
                      <td>
                        <Button size="sm" variant="ghost" onClick={() => selecionar(grupo.id)}>
                          {ativo ? "Em análise" : "Conferir"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          </div>

          {grupoSelecionado && (
            <Distribuir
              key={`${grupoSelecionado.id}:${grupoSelecionado.versao}`}
              grupo={grupoSelecionado}
              folhaId={folhaId}
              historico={historicoCompleto.filter((item) => item.chave === grupoSelecionado.chave)}
              onSuccess={atualizar}
            />
          )}
        </>
      )}
    </section>
  );
}

function ResumoValor({ rotulo, centavos, destaque = false }: { rotulo: string; centavos: number; destaque?: boolean }) {
  return (
    <div className={`dg-total ${destaque ? "dg-total--pending" : ""}`}>
      <span>{rotulo}</span>
      <strong>{fmtBRL(centavos / 100)}</strong>
    </div>
  );
}

function Distribuir({ grupo: g, folhaId, historico, onSuccess }: {
  grupo: DescontoGrupo;
  folhaId: string;
  historico: DescontoGrupo[];
  onSuccess: () => void;
}) {
  const [valores, setValores] = useState<Record<string, string>>({});
  const [justificativa, justificar] = useState("");
  const [reconhecer, reconhecerDivergencias] = useState(false);
  const centavos = (valor: string) => /^\d+(?:[,.]\d{1,2})?$/.test(valor)
    ? Math.round(Number(valor.replace(",", ".")) * 100)
    : NaN;
  const alocacoes = g.candidatos.map((candidato) => ({
    adf: candidato.adf,
    valorCentavos: centavos(valores[candidato.adf] ?? ""),
  }));
  const soma = alocacoes.reduce(
    (total, alocacao) => total + (Number.isFinite(alocacao.valorCentavos) ? alocacao.valorCentavos : 0),
    0,
  );
  const pago = valorPago(g);
  const saldo = pago - soma;
  const valido = alocacoes.every((alocacao) =>
    Number.isSafeInteger(alocacao.valorCentavos)
    && alocacao.valorCentavos >= 0
    && alocacao.valorCentavos <= g.candidatos.find((candidato) => candidato.adf === alocacao.adf)!.previstoCentavos,
  ) && saldo === 0;
  const podeConfirmar = !g.bloqueios.length
    && valido
    && !!justificativa.trim()
    && (!g.divergencias.length || reconhecer);
  const problemas = [...g.bloqueios, ...g.divergencias];
  const origem = g.origens[0];
  const mutation = useMutation({
    mutationFn: () => atlas.gruposDesconto.confirmar(folhaId, g.id, {
      versao: g.versao,
      alocacoes,
      justificativa,
      reconhecerDivergencias: reconhecer,
    }),
    onSuccess,
  });
  const reavaliacao = useMutation({
    mutationFn: () => atlas.gruposDesconto.reavaliar(folhaId, g.id, g.versao),
    onSuccess,
  });

  return (
    <article className="dg-card dg-detail">
      <div className="dg-detail-heading">
        <div className="dg-detail-title">
          <h2>Distribuição <span>•</span> Matrícula {origem?.matricula}</h2>
          <Estado grupo={g} />
        </div>
        <span className="dg-detail-caption">Competência {competenciaLegivel(g.competencia)}</span>
      </div>

      <div className="dg-origin-list">
        {g.origens.map((item, indice) => (
          <p key={`${item.arquivo}:${item.linha}:${indice}`}>
            <strong>{item.arquivo}</strong><span>•</span> Linha {item.linha}<span>•</span>
            Folha {item.codfp}/{item.ff}<span>•</span> OBS: {item.obs || "não informado"}<span>•</span>
            Informado: {fmtBRL(item.valorCentavos / 100)}<span>•</span> Motivo: {item.motivo || "não informado"}
          </p>
        ))}
      </div>

      <div className="dg-workspace">
        <div className="dg-contract-column">
          {problemas.length > 0 && (
            <div className="dg-issues" role="status">
              <div className="dg-issues-title"><span aria-hidden="true">⚠</span><strong>Pendências identificadas</strong></div>
              <ul>{problemas.map((mensagem, indice) => <li key={`${mensagem}:${indice}`}>{mensagem}</li>)}</ul>
            </div>
          )}

          <div className="dg-subheading">
            <h3>Contratos para distribuição</h3>
            {g.candidatos.length > 0 && <span>{g.candidatos.length} contrato{g.candidatos.length === 1 ? "" : "s"}</span>}
          </div>

          {g.candidatos.length === 0 ? (
            <div className="dg-empty-contracts">
              <span className="dg-document-icon" aria-hidden="true">▤</span>
              <div><strong>Nenhum contrato elegível</strong><p>Corrija o cadastro ou a rubrica e reavalie para continuar.</p>
                {g.estado === "pendente" && (
                  <Button size="sm" variant="ghost" disabled={reavaliacao.isPending} onClick={() => reavaliacao.mutate()}>
                    {reavaliacao.isPending ? "Reavaliando…" : "Reavaliar após correção"}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="dg-table-scroll dg-contract-table-wrap">
              <table className="dg-table dg-contract-table">
                <thead><tr><th>Contrato</th><th>Parcela</th><th className="dg-money">Previsto</th><th>Destinar ao contrato</th></tr></thead>
                <tbody>{g.candidatos.map((candidato) => (
                  <tr key={candidato.adf}>
                    <td className="dg-mono">{candidato.adf}</td>
                    <td>{candidato.parcela}ª</td>
                    <td className="dg-money">{fmtBRL(candidato.previstoCentavos / 100)}</td>
                    <td>{g.estado === "pendente" ? (
                      <div className="dg-money-input">
                        <span>R$</span>
                        <input
                          aria-label={`Valor para ${candidato.adf}`}
                          inputMode="decimal"
                          placeholder="0,00"
                          value={valores[candidato.adf] ?? ""}
                          onChange={(evento) => setValores({ ...valores, [candidato.adf]: evento.target.value })}
                        />
                      </div>
                    ) : fmtBRL((g.alocacoes.find((item) => item.adf === candidato.adf)?.valorCentavos ?? 0) / 100)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {g.estado === "pendente" && g.candidatos.length > 0 && (
            <Button size="sm" variant="ghost" disabled={reavaliacao.isPending} onClick={() => reavaliacao.mutate()}>
              {reavaliacao.isPending ? "Reavaliando…" : "Reavaliar contratos"}
            </Button>
          )}
          {reavaliacao.error && <p className="dg-inline-error" role="alert">{reavaliacao.error.message}</p>}
        </div>

        <aside className="dg-confirm-panel">
          <span className="dg-confirm-label">Saldo desta matrícula</span>
          <strong className={saldo < 0 ? "dg-negative" : undefined}>{fmtBRL(saldo / 100)}</strong>
          <div className="dg-progress" aria-hidden="true"><span style={{ width: `${pago > 0 ? Math.min(100, Math.max(0, soma / pago * 100)) : 100}%` }} /></div>
          <small>{fmtBRL(soma / 100)} de {fmtBRL(pago / 100)} distribuídos</small>

          {g.estado === "pendente" ? (
            <>
              <label className="dg-field">Justificativa
                <textarea
                  value={justificativa}
                  maxLength={2000}
                  placeholder="Descreva o motivo da distribuição…"
                  onChange={(evento) => justificar(evento.target.value)}
                />
              </label>
              {g.divergencias.length > 0 && (
                <label className="dg-check">
                  <input type="checkbox" checked={reconhecer} onChange={(evento) => reconhecerDivergencias(evento.target.checked)} />
                  <span>Conferi e reconheço as divergências apresentadas.</span>
                </label>
              )}
              {!podeConfirmar && (
                <div className="dg-lock-hint">
                  <span aria-hidden="true">▣</span>
                  {g.bloqueios.length
                    ? "Corrija os bloqueios e reavalie para continuar."
                    : saldo !== 0
                      ? "Distribua exatamente o saldo informado pela ALECE."
                      : !justificativa.trim()
                        ? "Informe a justificativa da distribuição."
                        : "Reconheça as divergências para confirmar."}
                </div>
              )}
              <Button
                className="dg-confirm-button"
                disabled={mutation.isPending || !podeConfirmar}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? "Confirmando…" : "Confirmar distribuição"}
              </Button>
              {mutation.error && <p className="dg-inline-error" role="alert">{mutation.error.message}</p>}
            </>
          ) : (
            <div className="dg-complete-note"><span aria-hidden="true">✓</span><div><strong>Distribuição concluída</strong><p>{g.decisao === "automatica" ? "Composição conferida automaticamente." : "Composição confirmada pelo operador."}</p></div></div>
          )}
        </aside>
      </div>

      <details className="dg-history">
        <summary><span aria-hidden="true">◴</span><strong>Histórico</strong><span>{historico.length} registro{historico.length === 1 ? "" : "s"}</span></summary>
        <div className="dg-history-list">{[...historico].reverse().map((item) => (
          <div key={item.id}>
            <strong>Versão {item.versao}</strong>
            <span>{item.estado}</span><span>{item.decisao ?? "aguardando decisão"}</span>
            <span>{item.responsavel ?? "—"}</span><time>{new Date(item.distribuidoEm ?? item.criadoEm).toLocaleString("pt-BR")}</time>
            {item.justificativa && <p>{item.justificativa}</p>}
          </div>
        ))}</div>
      </details>
    </article>
  );
}
